/**
 * The Roads map's tile address as a Teslyn Cloud setting (N19, decision 641, Q562 B follow-up: "now"), so a tenant
 * can switch to a paid OpenStreetMap host (MapTiler, Stadia, Thunderforest) without a release. Kept as `roads_tiles`
 * in `tenant.theme` (`{ template, attribution }`), set by a tenant admin in Teslyn Cloud, Settings, "Roads map", and
 * reaching Teslyn Field with the tenant row it already syncs. Not set: OpenStreetMap's own tiles, as before.
 *
 * A paid host's key, when its address needs one, is part of the address the tenant admin enters: every staff member
 * of the organisation can read the tenant row (Teslyn Field fetches the tiles itself), so a host whose key must stay
 * secret belongs behind the provider proxy instead (docs/specs/map.md §19.9 q7).
 */

/** A Roads tile address and its credit. */
export interface RoadsTiles {
  /** `https://…/{z}/{x}/{y}…`; `{s}` is not supported (one host). */
  readonly template: string;
  /** The credit shown on the map ("© MapTiler © OpenStreetMap contributors"). */
  readonly attribution: string;
  /** Where the credit links to, or null. */
  readonly copyrightUrl: string | null;
  /** True when the tenant set it; false for OpenStreetMap's own tiles. */
  readonly custom: boolean;
}

/** OpenStreetMap's own tiles: the Roads map when the tenant has set nothing (`tile-cache.ts`, osm-standard). */
export const ROADS_TILES_DEFAULT: RoadsTiles = {
  template: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '© OpenStreetMap contributors',
  copyrightUrl: 'https://www.openstreetmap.org/copyright',
  custom: false,
};

export const ROADS_TEMPLATE_MAX = 500;
export const ROADS_ATTRIBUTION_MAX = 200;

/**
 * Why a Roads tile address cannot be used, in words, or null when it can: an https address with `{z}`, `{x}` and `{y}`
 * once each, a host, no spaces, at most 500 characters; a credit of 3 to 200 characters that names OpenStreetMap
 * (its licence asks for it on every map of its data).
 */
export function checkRoadsTiles(t: {
  readonly template: string;
  readonly attribution: string;
}): string | null {
  const url = t.template.trim();
  if (url === '') return 'Enter the tile address.';
  if (url.length > ROADS_TEMPLATE_MAX)
    return `The tile address is longer than ${ROADS_TEMPLATE_MAX} characters.`;
  if (/\s/.test(url)) return 'The tile address cannot hold spaces.';
  if (!/^https:\/\/[a-z0-9.-]+\.[a-z]{2,}(:\d+)?\//i.test(url))
    return 'The tile address must start with https:// and a host, for example https://api.maptiler.com/…';
  for (const p of ['{z}', '{x}', '{y}'])
    if (url.split(p).length !== 2) return `The tile address must hold ${p} once.`;
  if (url.includes('{s}')) return 'Use one host: {s} (a choice of servers) is not supported.';
  const credit = t.attribution.trim();
  if (credit.length < 3)
    return 'Enter the credit the host asks for, for example "© MapTiler © OpenStreetMap contributors".';
  if (credit.length > ROADS_ATTRIBUTION_MAX)
    return `The credit is longer than ${ROADS_ATTRIBUTION_MAX} characters.`;
  if (!/openstreetmap/i.test(credit))
    return 'The credit must name OpenStreetMap ("© OpenStreetMap contributors").';
  return null;
}

/**
 * The Roads map of a tenant: its `theme.roads_tiles` when set and valid, else OpenStreetMap's own tiles. The theme
 * as PostgREST returns it (an object) or as PowerSync delivers it (JSON text).
 */
export function roadsTiles(stored: unknown): RoadsTiles {
  let theme = stored;
  if (typeof stored === 'string') {
    try {
      theme = JSON.parse(stored) as unknown;
    } catch {
      return ROADS_TILES_DEFAULT;
    }
  }
  const t = theme && typeof theme === 'object' ? (theme as Record<string, unknown>)['roads_tiles'] : null;
  if (!t || typeof t !== 'object') return ROADS_TILES_DEFAULT;
  const r = t as Record<string, unknown>;
  const template = typeof r['template'] === 'string' ? r['template'].trim() : '';
  const attribution = typeof r['attribution'] === 'string' ? r['attribution'].trim() : '';
  if (checkRoadsTiles({ template, attribution }) !== null) return ROADS_TILES_DEFAULT;
  return { template, attribution, copyrightUrl: null, custom: true };
}

/** The address of one tile. */
export function roadsTileUrl(r: Pick<RoadsTiles, 'template'>, z: number, x: number, y: number): string {
  return r.template.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
}
